import {
  ChangeEvent,
  ClipboardEvent,
  JSX,
  KeyboardEvent,
  useEffect,
  useRef,
} from "react";

import { cva, VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

import {
  VerificationCodeField,
  verificationCodeLabelFieldVariants,
} from "../ui/verification-code-field";

const ulThemeFloatingLabelFieldVariants = cva(
  [
    // Base Background & Colors
    "theme-universal:bg-input-bg",
    "theme-universal:text-input-text",

    // Border Styling
    "theme-universal:border-(length:--ul-theme-border-input-border-weight)",
    "theme-universal:border-input-border",

    // Border Radius
    "theme-universal:rounded-input",

    // Typography - Input Text
    "theme-universal:text-(length:--ul-theme-font-body-text-size)",
    "theme-universal:font-body-text",

    // Placeholder Styling
    "theme-universal:placeholder:text-input-labels",
    "theme-universal:[&_input]:placeholder:text-input-labels",
    "theme-universal:placeholder:text-(length:--ul-theme-font-input-labels-size)",
    "theme-universal:[&_input]:placeholder:text-(length:--ul-theme-font-input-labels-size)",
    "theme-universal:placeholder:font-input-label",
    "theme-universal:[&_input]:placeholder:font-input-label",

    // Floating Label Background
    "theme-universal:[&_label]:bg-input-bg",

    // Floating Label - Base Styling (applies to all labels)
    "theme-universal:[&_label]:text-input-labels",
    "theme-universal:[&_label]:text-(length:--ul-theme-font-input-labels-size)",
    "theme-universal:[&_label]:font-input-label",
  ],
  {
    variants: {
      themeState: {
        default: [
          // Focus States - Default
          "theme-universal:focus-within:border-base-focus",
          "theme-universal:focus-within:ring-0",

          // Focus Label States - Override label color when focused
          "theme-universal:focus-within:[&_label]:text-base-focus",
        ],
        error: [
          // Error States
          "theme-universal:text-error",
          "theme-universal:border-error",
          "theme-universal:focus-within:border-error",
          "theme-universal:focus-within:ring-0",

          // Error Label States - Force override using color CSS property
          "theme-universal:[&_label]:text-error",
        ],
      },
    },
    defaultVariants: {
      themeState: "default",
    },
  }
);

type VerificationCodeInputProps = {
  className?: string;
  isLoading?: boolean;
  error?: boolean;
  otp: string[];
  setOtp: (otp: string[]) => void;
  onComplete: (code: string) => Promise<void>;
  size?: VariantProps<typeof verificationCodeLabelFieldVariants>["size"];
  variant?: VariantProps<typeof verificationCodeLabelFieldVariants>["variant"];
};

export const ULThemeVerificationCodeInput = ({
  isLoading = false,
  otp,
  setOtp,
  onComplete,
  className,
  size = "default",
  error,
}: VerificationCodeInputProps): JSX.Element => {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  // Generate theme classes using CVA - automatically use error state when error prop is true
  const themeOverrides = ulThemeFloatingLabelFieldVariants({
    themeState: error ? "error" : "default",
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isLoading && inputs.current[0]) {
      timer = setTimeout(() => {
        inputs.current[0]?.focus();
        inputs.current[0]?.select();
      }, 100);
    }
    return () => clearTimeout(timer);
  }, [isLoading]);

  const moveToNextInput = (index: number): void => {
    if (index < 5 && inputs.current[index + 1]) {
      inputs.current[index + 1]?.focus();
      inputs.current[index + 1]?.select();
    }
  };

  const moveToPreviousInput = (index: number): void => {
    if (index > 0 && inputs.current[index - 1]) {
      inputs.current[index - 1]?.focus();
      inputs.current[index - 1]?.select();
    }
  };

  const handleChange = async (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ): Promise<void> => {
    const { value: inputValue } = e.target;
    if (!/^[0-9]?$/.test(inputValue)) return;

    const newOtp = [...otp];
    newOtp[index] = inputValue;
    setOtp(newOtp);

    if (inputValue && index < 5) {
      moveToNextInput(index);
    }

    if (inputValue && index === 5 && newOtp.every((digit) => digit !== "")) {
      await onComplete(newOtp.join(""));
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    index: number
  ): void => {
    if (e.key === "Backspace") {
      if (!otp[index]) {
        moveToPreviousInput(index);
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
      return;
    }

    if (e.key === "ArrowRight") {
      e.preventDefault();
      moveToNextInput(index);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      moveToPreviousInput(index);
    }
  };

  const handlePaste = (
    e: ClipboardEvent<HTMLInputElement>,
    startIndex: number
  ): void => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("Text").slice(0, 6 - startIndex);
    if (!/^\d+$/.test(pasteData)) return;

    const newOtp = [...otp];
    pasteData.split("").forEach((char, i) => {
      if (startIndex + i < 6) {
        newOtp[startIndex + i] = char;
      }
    });
    setOtp(newOtp);

    const focusIndex = Math.min(startIndex + pasteData.length, 5);
    if (inputs.current[focusIndex]) {
      inputs.current[focusIndex]?.focus();
    }

    if (newOtp.join("").length === 6) {
      onComplete(newOtp.join(""));
    }
  };

  return (
    <div className="flex gap-2">
      {otp.map((digit, index) => (
        <VerificationCodeField
          key={`verification-digit-${index + 1}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus={true}
          autoComplete="one-time-code"
          onPaste={(e: ClipboardEvent<HTMLInputElement>) =>
            handlePaste(e, index)
          }
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(e, index)
          }
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange(e, index)
          }
          maxLength={1}
          value={digit}
          ref={(el: HTMLInputElement | null) => {
            inputs.current[index] = el;
          }}
          aria-label={`Digit ${index + 1} of verification code`}
          className={cn(className, themeOverrides)}
          size={size}
          error={error}
        />
      ))}
    </div>
  );
};
